/**
 * Разделы приложения — один список на все меню.
 *
 * Нижняя панель телефона, страница «Ещё» и боковое меню компьютера берут
 * разделы отсюда. Пока список жил в каждом меню свой, новый раздел легко было
 * добавить в одно и забыть в другом.
 */

import {
  Baby,
  CalendarDays,
  CheckSquare,
  Clapperboard,
  HeartPulse,
  LayoutGrid,
  Moon,
  Repeat2,
  ShoppingBasket,
  UserRound,
  type LucideIcon,
} from 'lucide-react';

export interface NavSection {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Короткое пояснение: по одному названию не всегда понятно, что внутри. */
  description?: string;
}

/**
 * Пять разделов нижней панели — предел, при котором подписи остаются
 * читаемыми на экране шириной 320 px. Остальное убрано в «Ещё».
 *
 * Группировка следует методологии, а не техническому делению:
 * привычки живут внутри «Целей» (привычка — способ достичь цели),
 * таблетница — внутри «Здоровья».
 */
export const PRIMARY_SECTIONS: NavSection[] = [
  { to: '/calendar', label: 'Календарь', icon: CalendarDays },
  { to: '/tasks', label: 'Задачи', icon: CheckSquare },
  /*
    Раздел называется по тому, ради чего в него заходят каждый день. Цели живут
    там же соседней вкладкой — их открывают редко. Адрес оставлен прежним:
    человеку он не виден, а переименование сломало бы сохранённые ссылки.
  */
  { to: '/goals', label: 'Привычки', icon: Repeat2 },
  { to: '/health', label: 'Здоровье', icon: HeartPulse },
  { to: '/more', label: 'Ещё', icon: LayoutGrid },
];

/**
 * Разделы, не поместившиеся в нижнюю панель.
 *
 * Порядок — по частоте использования: список покупок открывают несколько раз
 * в неделю, профиль — несколько раз в год.
 */
export const MORE_SECTIONS: NavSection[] = [
  // Съёмный раздел: живёт целиком в `src/pages/pregnancy/`. Чтобы убрать,
  // удалите эту запись и папку раздела — см. README рядом с ней.
  {
    to: '/pregnancy',
    label: 'Беременность',
    description: 'Таймер схваток, сумка в роддом и план родов',
    icon: Baby,
  },
  {
    to: '/shopping',
    label: 'Покупки',
    description: 'Списки с датой похода в магазин и группировкой по отделам',
    icon: ShoppingBasket,
  },
  {
    to: '/media',
    label: 'Фильмы и книги',
    // «Библиотека» и «читаю» описывали одни книги, хотя раздел про фильмы и
    // книги сразу. Фильмы — первыми: их смотрят чаще.
    description: 'Что хочу посмотреть и прочитать, оценки и отзывы',
    icon: Clapperboard,
  },
  {
    to: '/profile/routine',
    label: 'Режим дня',
    description: 'Сон и приёмы пищи — основа, вокруг которой планируется день',
    icon: Moon,
  },
  {
    to: '/profile',
    label: 'Профиль',
    description: 'Личные данные, планирование, оформление и выход из аккаунта',
    icon: UserRound,
  },
];
